import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { HttpService } from '@services/http.service';
import { Observable } from 'rxjs';
import { UserDevice } from '@models/user-device';
import { Device, Attribute, Limit } from '@models/device';

@Injectable({
  providedIn: 'root',
})
export class DevicesService extends HttpService {
  readonly USER_DEVICES_API = `${environment.apiUrl}/user-devices`;
  readonly DEVICES_API = `${environment.apiUrl}/devices`;

  getUserDevices(userId: string): Observable<UserDevice[]> {
    const defaultOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.storageService.get('tok')}`,
      }),
    };
    return this.get(`${this.USER_DEVICES_API}/list/${userId}`, defaultOptions);
  }

  getUserDevice(userDeviceId: number, loadExtraInfo?: boolean): Observable<UserDevice> {
    let params = new HttpParams();

    if (loadExtraInfo !== undefined) {
      params = params.set('loadExtraInfo', loadExtraInfo.toString());
    }

    const defaultOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.storageService.get('tok')}`,
      }),
      params,
    };
    return this.get(`${this.USER_DEVICES_API}/${userDeviceId}`, defaultOptions);
  }

  getUserDeviceByHardwareId(hardwareId: string): Observable<UserDevice | Device | any> {
    const defaultOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.storageService.get('tok')}`,
      }),
    };
    return this.get(`${this.USER_DEVICES_API}/hardwareId/${hardwareId}`, defaultOptions);
  }

  getUserDeviceByUserId(userId: string, deviceId: string): Observable<UserDevice> {
    const defaultOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.storageService.get('tok')}`,
      }),
    };
    return this.get(`${this.USER_DEVICES_API}/${userId}/device/${deviceId}`, defaultOptions);
  }

  getDevice(deviceId: string): Observable<Device> {
    const defaultOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.storageService.get('tok')}`,
      }),
    };
    return this.get(`${this.DEVICES_API}/${deviceId}`, defaultOptions);
  }



  suscribeUserDevice(userDeviceId: number): Observable<any> {
    const defaultOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.storageService.get('tok')}`,
      }),
    };
    return this.put(
      `${this.USER_DEVICES_API}/${userDeviceId}/suscribe`,
      {},
      defaultOptions
    );
  }

  unsuscribeUserDevice(userDeviceId: number): Observable<any> {
    const defaultOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.storageService.get('tok')}`,
      }),
    };
    return this.put(
      `${this.USER_DEVICES_API}/${userDeviceId}/unsuscribe`,
      {},
      defaultOptions
    );
  }

  updateDevice(deviceId: string, data: any): Observable<any> {
    const options = this.createHeaders();
    return this.put(`${this.DEVICES_API}/${deviceId}`, data, options);
  }

  updateCurrentDetails(detailsUpdated, userDeviceId): Observable<any> {
    const defaultOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.storageService.get('tok')}`,
      }),
    };
    return this.put(
      `${this.USER_DEVICES_API}/${userDeviceId}`,
      detailsUpdated,
      defaultOptions
    );
  }

  assignUserDevice(deviceData): Observable<any> {
    const options = this.createHeaders();
    return this.post(`${this.USER_DEVICES_API}/`, deviceData, options);
  }

  saveAttributes(deviceId: string, attribute: Attribute[]): Observable<any> {
    const options = this.createHeaders();
    return this.post(`${this.DEVICES_API}/${deviceId}/sensors/attributes`, attribute, options);
  }

  saveLimit(deviceId: string, limit: Limit): Observable<any> {
    const options = this.createHeaders();
    return this.post(`${this.DEVICES_API}/${deviceId}/limits`, limit, options);
  }

  updateLimit(deviceId: string, limitId: string, limit: Limit): Observable<any> {
    const options = this.createHeaders();
    return this.put(`${this.DEVICES_API}/${deviceId}/limits/${limitId}`, limit, options);
  }

  deleteLimit(deviceId: string, limitId: string): Observable<any> {
    const options = this.createHeaders();
    return this.delete(`${this.DEVICES_API}/${deviceId}/limits/${limitId}`, options);
  }

  byUserIdBasicInfo(userId): Observable<any> {
    const options = this.createHeaders();
    return this.get(`${this.USER_DEVICES_API}/list/${userId}/basic`, options);
  }
}
